import { DatePicker, notification } from 'antd';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import Button from '../../../../../../components/Button';
import { ButtonCancel } from '../../../../../../components/Button/styles';
import { formatarData } from '../../../../../../helpers/data';
import apiShipper from '../../../../../../services/apiShipper';
import { useAgendamentoAlteracaoContext } from '../../../../Contextos/AgendamentoAlteracaoContexto';
import StatusAlteracaoEnum from '../../../../Enum/StatusAlteracaoEnum';
import StatusLoteEnum from '../../../../Enum/StatusLoteEnum';
import TipoAlteracaoEnum from '../../../../Enum/TipoAlteracaoEnum';
import {
  AgendamentoContainer,
  AgendamentoInfo,
  CustomModal,
  FieldContainer,
} from '../styles';

const FinalizarModal = ({
  visible,
  setVisible,
  agendamento,
  setAgendamento,
  actionPayload,
}) => {
  const [confirmLoading, setConfirmLoading] = useState({
    agora: false,
    agendar: false,
    cancelar: false,
  });
  const [dataAgendamento, setDataAgendamento] = useState();
  const { removeAgendamentoAlteracao } = useAgendamentoAlteracaoContext();
  const history = useHistory();

  const resetLoading = () => {
    setConfirmLoading({
      agora: false,
      agendar: false,
      cancelar: false,
    });
  };

  const handleFinalizarAgora = async () => {
    setConfirmLoading({ agora: true, agendar: false, cancelar: false });

    try {
      const payload = {
        data_alteracao: moment().format(),
        status_lote: StatusLoteEnum.FINALIZADO,
        ...actionPayload,
      };

      await apiShipper.post('alteracao-demanda', payload);

      notification.success({
        message: 'Sucesso!',
        description: 'Lotes finalizados com sucesso.',
      });
      history.push({
        pathname: `/detalhesDemanda/${actionPayload.id_demanda}`,
      });
    } catch {
      notification.error({
        message: 'Ocorreu um erro inesperado ao finalizar os lotes',
        description: 'Se o problema persistir, entre em contato com o suporte',
      });
    } finally {
      resetLoading();
      setVisible();
    }
  };

  const handleAgendar = async () => {
    setConfirmLoading({ agora: false, agendar: true, cancelar: false });

    try {
      const payload = {
        data_alteracao: moment(dataAgendamento)
          .utc(true)
          .format(),
        status_lote: StatusLoteEnum.FINALIZADO,
        tipo_alteracao: TipoAlteracaoEnum.FINALIZAR,
        ...actionPayload,
      };

      await apiShipper.post('alteracao-demanda', payload);

      notification.success({
        message: 'Sucesso!',
        description: 'Finalização de lotes agendada com sucesso.',
      });

      setAgendamento({ data: dataAgendamento });
      history.push({
        pathname: `/detalhesDemanda/${actionPayload.id_demanda}`,
      });
    } catch {
      notification.error({
        message:
          'Ocorreu um erro inesperado ao agendar a finalização dos lotes',
        description: 'Se o problema persistir, entre em contato com o suporte',
      });
    } finally {
      setVisible();
      resetLoading();
    }
  };

  const handleCancelAgendamento = async () => {
    setConfirmLoading({ agora: false, agendar: false, cancelar: true });

    try {
      await apiShipper.put('alteracao-demanda', {
        id_demanda: actionPayload.id_demanda,
        status: StatusAlteracaoEnum.status.CANCELADO,
        tipo_alteracao: TipoAlteracaoEnum.FINALIZAR,
      });

      notification.success({
        message: 'Sucesso!',
        description: 'Agendamento de finalização cancelado com sucesso.',
      });

      removeAgendamentoAlteracao(agendamento);
      setAgendamento({ id: null, data: null });
      setDataAgendamento(null);
    } catch (error) {
      notification.error({
        message: 'Ocorreu um erro inesperado ao cancelar o agendamento',
        description: 'Se o problema persistir, entre em contato com o suporte',
      });
    } finally {
      resetLoading();
      setVisible();
    }
  };

  useEffect(() => {
    if (!visible) setDataAgendamento(null);
  }, [visible]);

  return (
    <CustomModal
      closable={false}
      title="Finalizar todos os lotes"
      visible={visible}
      footer={[
        <Button key="fechar" type="secondary" onClick={() => setVisible()}>
          Fechar
        </Button>,
        <Button
          key="alterar"
          type="secondary"
          loading={confirmLoading.agora}
          onClick={handleFinalizarAgora}
          disabled={dataAgendamento}
        >
          Finalizar agora
        </Button>,
        <Button
          key="agendar"
          type="primary"
          loading={confirmLoading.agendar}
          onClick={handleAgendar}
          disabled={!dataAgendamento}
        >
          Agendar
        </Button>,
      ]}
    >
      <FieldContainer>
        <p>
          Essa ação não poderá ser desfeita. Notificaremos a alteração para as
          transportadoras.
        </p>
      </FieldContainer>
      <FieldContainer>
        <p>Agendar alteração</p>
        <AgendamentoContainer>
          <DatePicker
            data-testid="data-agendamento"
            value={dataAgendamento}
            name="dataAgendamento"
            format="DD/MM/YYYY"
            disabled={agendamento.data}
            disabledDate={current => current.isBefore(moment())}
            style={{ width: '60%' }}
            onChange={data => setDataAgendamento(data)}
          />
          <div style={{ width: '10px' }} />
          {agendamento.data && (
            <ButtonCancel
              loading={confirmLoading.cancelar}
              onClick={handleCancelAgendamento}
            >
              Cancelar
            </ButtonCancel>
          )}
        </AgendamentoContainer>
      </FieldContainer>
      {agendamento.data && (
        <AgendamentoInfo>
          <p>{`Finalização agendada para ${formatarData(agendamento.data)}`}</p>
        </AgendamentoInfo>
      )}
    </CustomModal>
  );
};

FinalizarModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  agendamento: PropTypes.shape({
    data: PropTypes.string,
  }),
  setAgendamento: PropTypes.func.isRequired,
  actionPayload: PropTypes.shape({
    id_demanda: PropTypes.number,
    cliente_razao_social: PropTypes.string,
    origem: PropTypes.string,
    destino: PropTypes.string,
    email_filiais: PropTypes.arrayOf(PropTypes.string),
  }),
};

FinalizarModal.defaultProps = {
  agendamento: { data: null },
  actionPayload: null,
};

export default FinalizarModal;
